import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import "date-fns";
import enLocale from "date-fns/locale/en-US";
import huLocale from "date-fns/locale/hu";
import { OcCustomPickerIcon } from "oc/components";

interface OcTimePickerProps {
  error?: boolean;
  helperText?: string;
  id?: string;
  label?: string;
  minDate?: Date;
  onChange?: (value: Date | null) => void;
  value?: Date | null;
  onFocus?: () => void;
  width?: string;
  isMobile?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  timePeriod?: number;
}

export const OcTimePicker: React.FC<OcTimePickerProps> = ({
  error,
  helperText,
  id,
  label,
  minDate,
  onChange,
  value,
  onFocus,
  width,
  isMobile,
  fullWidth = false,
  disabled,
  timePeriod,
}) => {
  const { i18n } = useTranslation();
  const theme = useTheme();

  let lang: "en" | "hu" =
    i18n && i18n.language
      ? (i18n.language.substr(0, 2).toLowerCase() as "en" | "hu")
      : "hu";

  const getLocaleMap = () => {
    const localeMap = {
      en: enLocale,
      hu: huLocale,
    };

    return localeMap[lang] || huLocale;
  };

  let parsedWith = width ? width : "204px";
  if (fullWidth || isMobile) {
    parsedWith = "100%";
  }

  let timeStepValue;
  switch (timePeriod) {
    case 10:
      timeStepValue = 10;
      break;
    case 30:
      timeStepValue = 30;
      break;
    case undefined:
    case 0:
    default:
      timeStepValue = 1;
      break;
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={getLocaleMap()}
    >
      <TimePicker
        ampm={false}
        minTime={minDate}
        disabled={disabled}
        value={value}
        onChange={onChange}
        slots={{
          openPickerIcon: OcCustomPickerIcon,
        }}
        slotProps={{
          textField: {
            error: error,
            helperText: helperText,
            id: id,
            name: id,
            label: label,
            variant: "outlined",
            margin: "normal",
            onFocus: onFocus,
            style: { width: parsedWith || (isMobile ? "100%" : "204px") },
          },
          openPickerButton: {
            sx: {
              backgroundColor: theme.palette.gray20.main,
              marginRight: "2px",
              padding: "12px",
            },
          },
          actionBar: {
            actions: [],
          },
        }}
        timeSteps={{ minutes: timeStepValue }}
        desktopModeMediaQuery="@media (min-width: 0px)"
      />
    </LocalizationProvider>
  );
};

export default OcTimePicker;
